import * as React from "react";
import { useNavigate } from "react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableColumnDefinition,
  TableColumnSizingOptions,
  TableHeader,
  TableHeaderCell,
  TableRow,
  createTableColumn,
  useTableColumnSizing_unstable,
  useTableFeatures,
  Checkbox,
  Button,
  makeStyles,
  Input,
  Label,
  tokens,
  Dropdown,
  Option,
  RadioGroup,
  Radio,
  Combobox,
  Tooltip,
} from "@fluentui/react-components";
import {
  EditRegular,
  DeleteRegular,
  InfoRegular,
  VehicleCarProfile20Regular,
  Home20Regular,
  Wrench20Regular,
  VehicleTruck20Regular,
  Earth20Regular,
  Money20Regular,
  Briefcase20Regular,
  ShieldKeyhole20Regular,
  AppGeneric20Regular,
} from "@fluentui/react-icons";
import { kennzahlenForm, kfzForm, evalCond, type Field } from "../data/data";
import { Switch, Textarea, Checkbox as FluentCheckbox } from "@fluentui/react-components";
import { ContactSelector } from "./ContactSelector";
import { apiUrl } from "../utils/api";
import InfiniteScroll from "react-infinite-scroll-component";
import { useWindowManager } from "./windowManager/WindowManagerContext";
import { openContractFormWindow } from "./windowManager/ContractFormWindow";
import CommandBar from "./CommandBar";

// Typdefinition für Risiko-Objekte
export interface Risiko {
  RisikoID: number;
  RisikoOrt: string;
  VertragID: number;
  KontaktID: number;
  ZweigID: number;
  Zweig: string;
  KontaktName: string;
  Vertragsnummer: string;
  /** Kennzeichen — Anschrift für Immobilie, KFZ-Kennzeichen, etc. */
  Kennzeichen?: string;
  /** Zusätzliche Fahrzeug-ID (nur KFZ). */
  FahrzeugID?: string;
  /** Team/Mitarbeiter, der das Risiko verantwortet. */
  KVerantwortlicher?: string;
}

type RiskKind = "kfz" | "immobilie" | "maschine" | "transport" | "umwelt" | "liquiditaet" | "cyber" | "sach" | "sonstiges";

const resolveRiskKind = (zweig: string): RiskKind => {
  const z = (zweig ?? "").toLowerCase();
  if (z.includes("kfz")) return "kfz";
  if (z.includes("immo") || z.includes("geb")) return "immobilie";
  if (z.includes("masch")) return "maschine";
  if (z.includes("transp")) return "transport";
  if (z.includes("umwelt")) return "umwelt";
  if (z.includes("liquid")) return "liquiditaet";
  if (z.includes("cyber")) return "cyber";
  if (z.includes("sach") || z.includes("haft")) return "sach";
  return "sonstiges";
};

const RISK_ICONS: Record<RiskKind, React.ComponentType<{ style?: React.CSSProperties }>> = {
  kfz: VehicleCarProfile20Regular,
  immobilie: Home20Regular,
  maschine: Wrench20Regular,
  transport: VehicleTruck20Regular,
  umwelt: Earth20Regular,
  liquiditaet: Money20Regular,
  cyber: ShieldKeyhole20Regular,
  sach: Briefcase20Regular,
  sonstiges: AppGeneric20Regular,
};

const RISK_ICON_COLORS: Record<RiskKind, string> = {
  kfz: "#b91c1c",
  immobilie: "#1e40af",
  maschine: "#57534e",
  transport: "#c2410c",
  umwelt: "#15803d",
  liquiditaet: "#ca8a04",
  cyber: "#0f172a",
  sach: "#7c5a3a",
  sonstiges: "#475569",
};

const useStyles = makeStyles({
  stripedRow: {
    backgroundColor: "#f2f2f2",
  },
  cellEllipsis: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "16px 0",
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    paddingBottom: "8px",
    marginTop: "16px",
  },
  fieldGrid: {
    gap: "16px"
  },
  matrixTable: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "10px",
    fontSize: "12px",
    "& th": {
      fontWeight: tokens.fontWeightSemibold,
      padding: "8px",
      textAlign: "center",
      borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    "& td": {
      padding: "4px",
      borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    }
  },
  matrixInput: {
    "& input": {
      textAlign: "center",
      padding: "2px 4px",
    },
    "&::after": {
      borderBottom: "none"
    }
  }
});

const EMP_FUNCTIONS = [
  "Büro/Verwaltung/Finanzen",
  "Marketing",
  "Vertrieb",
  "Entwickler",
  "Projektmanager",
  "Werkstatt/Produktion",
  "Arbeiten auf fremden Grundstücken",
  "Werkstudenten",
  "Sonstige"
];

const EMP_TYPES = ["V", "T", "A", "H", "P", "M", "GF", "F"];



export const KennzahlenRiskForm = ({ onCancel, onSave }: { onCancel: () => void, onSave: (data: any) => void }) => {
  const classes = useStyles();
  const [formData, setFormData] = React.useState<Record<string, any>>({});
  const [step, setStep] = React.useState(1);
  const [yearsBack, setYearsBack] = React.useState(1);

  const currentYear = new Date().getFullYear();

  const selectedYears = React.useMemo(() => {
    const years = [currentYear + 1, currentYear];
    for (let i = 1; i <= yearsBack; i++) {
      years.push(currentYear - i);
    }
    return years;
  }, [currentYear, yearsBack]);

  const handleChange = (name: string, year: number, value: any) => {
    setFormData(prev => ({ ...prev, [`${name}_${year}`]: value }));
  };

  if (step === 1) {
    return (
      <div className={classes.formContainer}>
        <div className={classes.sectionTitle}>Zeitraum festlegen</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '10px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Label weight="semibold">Standardjahre:</Label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button appearance="outline" disabled size="small">{currentYear} (Laufendes Jahr)</Button>
              <Button appearance="outline" disabled size="small">{currentYear + 1} (Kommendes Jahr)</Button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Label htmlFor="years-back-dropdown">Wie viele Jahre zurück?</Label>
            <Dropdown
              id="years-back-dropdown"
              placeholder="Bitte auswählen"
              onOptionSelect={(e, data) => setYearsBack(Number(data.optionValue))}
              value={yearsBack.toString()}
              selectedOptions={[yearsBack.toString()]}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                <Option key={n} value={n.toString()} text={`${n} ${n === 1 ? 'Jahr' : 'Jahre'}`}>{n} {n === 1 ? 'Jahr' : 'Jahre'}</Option>
              ))}
            </Dropdown>
            <div style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground4 }}>
              Es werden Daten für {2 + yearsBack} Jahre erfasst.
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
          <Button appearance="secondary" onClick={onCancel}>Abbrechen</Button>
          <Button appearance="primary" onClick={() => setStep(2)}>Weiter</Button>
        </div>
      </div>
    );
  }

  const activeYear = selectedYears[step - 2];
  const isLastStep = step === 1 + selectedYears.length;

  const getMatrixVal = (func: string, type: string) => {
    const val = formData[`employeesMatrix_${activeYear}`];
    const matrix = val ? JSON.parse(val) : {};
    return matrix[func]?.[type] ?? "";
  };

  const setMatrixVal = (func: string, type: string, val: string) => {
    const currentVal = formData[`employeesMatrix_${activeYear}`];
    const matrix = currentVal ? JSON.parse(currentVal) : {};
    if (!matrix[func]) matrix[func] = {};
    matrix[func][type] = parseInt(val) || 0;
    setFormData(prev => ({ ...prev, [`employeesMatrix_${activeYear}`]: JSON.stringify(matrix) }));
  };

  return (
    <div className={classes.formContainer}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className={classes.sectionTitle}>Kennzahlen für das Jahr {activeYear}</div>
        <div style={{ color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 }}>
          Schritt {step - 1} von {selectedYears.length}
        </div>
      </div>

      {kennzahlenForm.steps.map(stepObj => (
        <div key={stepObj.name}>
          <div style={{ fontWeight: tokens.fontWeightSemibold, marginBottom: '10px', marginTop: '10px' }}>{stepObj.name}</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${tokens.colorNeutralStroke1}` }}>
            <tbody>
              {stepObj.fields.map(field => {
                if (field.fieldType === 'EmployeeMatrix') {
                  return (
                    <tr key={field.name}>
                      <td colSpan={2} style={{ padding: '0', borderTop: `1px solid ${tokens.colorNeutralStroke1}` }}>
                        <div style={{ marginTop: '10px' }}>
                          <table className={classes.matrixTable}>
                            <thead>
                              <tr>
                                <th style={{ textAlign: 'left', width: '25%' }}>Funktion</th>
                                {EMP_TYPES.map(t => <th key={t}>{t}</th>)}
                              </tr>
                            </thead>
                            <tbody>
                              {EMP_FUNCTIONS.map(f => (
                                <tr key={f}>
                                  <td style={{ textAlign: 'left' }}>{f}</td>
                                  {EMP_TYPES.map(t => (
                                    <td key={t}>
                                      <input
                                        value={getMatrixVal(f, t)}
                                        onChange={(e) => setMatrixVal(f, t, e.target.value)}
                                        style={{
                                          width: '100%',
                                          border: 'none',
                                          borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
                                          textAlign: 'center',
                                          padding: '4px 0',
                                          outline: 'none',
                                          background: 'transparent',
                                          fontSize: '12px'
                                        }}
                                      />
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  );
                }

                const isCurrency = field.fieldType === 'CurrencyInput';
                const isNumber = field.fieldType === 'NumberInput' || (field.fieldType === 'TextInput' && (field.name.includes('Count') || field.name.includes('Km') || field.name.includes('Days')));
                const isAvgPersons = field.name === 'avgConcurrentPersons';

                return (
                  <tr key={field.name} style={{ borderTop: isAvgPersons ? undefined : `1px solid ${tokens.colorNeutralStroke1}` }}>
                    <td style={{
                      padding: '8px 12px',
                      width: '40%',
                      borderRight: isAvgPersons ? undefined : `1px solid ${tokens.colorNeutralStroke1}`,
                      verticalAlign: 'middle',
                      fontWeight: isAvgPersons ? tokens.fontWeightSemibold : undefined
                    }}>
                      {field.label}
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <input
                          value={formData[`${field.name}_${activeYear}`] || ""}
                          onChange={(e) => handleChange(field.name, activeYear, e.target.value)}
                          placeholder={field.placeholder}
                          type={isNumber && !field.fieldType.includes('Text') ? "number" : "text"}
                          style={{
                            width: isAvgPersons ? '80px' : '100%',
                            marginLeft: isAvgPersons ? 'auto' : undefined,
                            display: isAvgPersons ? 'block' : undefined,
                            border: 'none',
                            borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
                            padding: '4px 0',
                            textAlign: isNumber || isCurrency || isAvgPersons ? 'right' : 'left',
                            outline: 'none',
                            background: 'transparent',
                            fontSize: tokens.fontSizeBase300
                          }}
                        />
                        {isCurrency && <span style={{ fontSize: tokens.fontSizeBase300 }}>€</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
        <Button
          appearance="subtle"
          onClick={() => setStep(step - 1)}
          style={{ marginRight: 'auto' }}
        >
          Zurück
        </Button>
        <Button appearance="secondary" onClick={onCancel}>Abbrechen</Button>
        {isLastStep ? (
          <Button appearance="primary" onClick={() => onSave(formData)}>Speichern</Button>
        ) : (
          <Button appearance="primary" onClick={() => setStep(step + 1)}>Weiter</Button>
        )}
      </div>
    </div>
  );
};


export const KFZRiskForm = ({ onCancel, onSave }: { onCancel: () => void, onSave: (data: any) => void }) => {
  const classes = useStyles();
  const [formData, setFormData] = React.useState<Record<string, any>>({});

  const handleChange = (name: string, value: any) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const [relations, setRelations] = React.useState<any[]>([]);

  React.useEffect(() => {
    const mainId = formData.mainDriverId;
    if (mainId && typeof mainId === 'number') {
      fetch(apiUrl(`contact/${mainId}`))
        .then(res => res.json())
        .then(data => {
          setRelations(data.relationsSrc || []);
        })
        .catch(err => console.error("Failed to fetch relations", err));
    } else {
      setRelations([]);
    }
  }, [formData.mainDriverId]);

  const renderField = (field: Field) => {
    const isVisible = field.visibleWhen ? evalCond(field.visibleWhen, formData) : true;
    if (!isVisible) return null;

    return (
      <div key={field.name} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Label>{field.label}</Label>
          {field.info && (
            <Tooltip content={field.info} relationship="label">
              <InfoRegular style={{ fontSize: '14px', color: tokens.colorNeutralForeground3, cursor: 'help' }} />
            </Tooltip>
          )}
        </div>
        {field.fieldType === 'Dropdown' && (
          <Dropdown
            placeholder={field.placeholder}
            onOptionSelect={(e, data) => handleChange(field.name, data.optionValue)}
            value={formData[field.name] || ""}
          >
            {field.content?.map(opt => <Option key={opt} value={opt} text={opt}>{opt}</Option>)}
          </Dropdown>
        )}
        {field.fieldType === 'RadioGroup' && (
          <RadioGroup
            value={formData[field.name] || ""}
            onChange={(e, data) => handleChange(field.name, data.value)}
          >
            <div style={{ display: 'flex', gap: '16px' }}>
              {field.content?.map(opt => (
                <Radio
                  key={opt}
                  value={opt}
                  label={opt}
                  onClick={() => {
                    if (formData[field.name] === opt) {
                      handleChange(field.name, "");
                    }
                  }}
                />
              ))}
            </div>
          </RadioGroup>
        )}
        {field.fieldType === 'TextInput' && (
          <Input
            value={formData[field.name] || ""}
            onChange={(e, d) => handleChange(field.name, d.value)}
            placeholder={field.placeholder}
          />
        )}
        {field.fieldType === 'CurrencyInput' && (
          <Input
            value={formData[field.name] || ""}
            onChange={(e, d) => handleChange(field.name, d.value)}
            placeholder={field.placeholder}
            contentAfter="€"
            type="number"
          />
        )}
        {field.fieldType === 'Checkbox' && (
          <FluentCheckbox
            checked={!!formData[field.name]}
            onChange={(e, d) => handleChange(field.name, d.checked)}
            label={field.placeholder}
          />
        )}
        {field.fieldType === 'Switch' && (
          <Switch
            checked={!!formData[field.name]}
            onChange={(e, d) => handleChange(field.name, d.checked)}
          />
        )}
        {field.fieldType === 'TextArea' && (
          <Textarea
            value={formData[field.name] || ""}
            onChange={(e, d) => handleChange(field.name, d.value)}
            placeholder={field.placeholder}
          />
        )}
        {field.fieldType === 'DateInput' && (
          <Input
            type="date"
            value={formData[field.name] || ""}
            onChange={(e, d) => handleChange(field.name, d.value)}
            placeholder={field.placeholder}
          />
        )}
        {field.fieldType === 'Combobox' && (
          <Combobox
            placeholder={field.placeholder}
            onOptionSelect={(e, data) => handleChange(field.name, data.optionValue)}
            onChange={(e) => handleChange(field.name, e.target.value)}
            value={formData[field.name] || ""}
            freeform
          >
            {field.content
              ?.filter(opt =>
                !formData[field.name] ||
                opt.toLowerCase().includes(formData[field.name].toLowerCase())
              )
              .map(opt => <Option key={opt} value={opt} text={opt}>{opt}</Option>)
            }
          </Combobox>
        )}
        {field.fieldType === 'ContactSelection' && (
          <ContactSelector
            label={field.label}
            onSelect={(contact) => {
              handleChange(field.name, contact.id);
              handleChange(`${field.name}_name`, `${contact.lastname}, ${contact.vorname ?? contact.firstName}`);
            }}
            selectedContact={formData[`${field.name}_name`] ? {
              lastname: formData[`${field.name}_name`].split(',')[0],
              vorname: formData[`${field.name}_name`].split(',')[1]?.trim()
            } : null}
          />
        )}
        {field.fieldType === 'RelationSelection' && (
          <Dropdown
            placeholder="Kontakt aus Verknüpfungen wählen"
            onOptionSelect={(e, data) => {
              const name = data.optionValue;
              const rel = relations.find(r => {
                const dest = r.contactDest;
                const dName = `${dest.lastname}, ${dest.vorname ?? dest.firstName}`;
                return dName === name;
              });
              if (rel) {
                handleChange(field.name, rel.contactDest.id);
                handleChange(`${field.name}_name`, name);
              }
            }}
            value={formData[`${field.name}_name`] || ""}
          >
            {relations
              .filter(rel => {
                const role = rel.relationType?.contactRelationType?.toLowerCase() || "";
                if (formData.driverCircle === "VN + Partner") {
                  return role.includes("partner") || role.includes("ehe");
                }
                if (formData.driverCircle === "VN + Kind" || formData.driverCircle === "Kinder") {
                  return role.includes("kind");
                }
                return true;
              })
              .map(rel => {
                const dest = rel.contactDest;
                const dName = `${dest.lastname}, ${dest.vorname ?? dest.firstName}`;
                const role = rel.relationType?.contactRelationType || "";
                return <Option key={dest.id} value={dName} text={dName}>{dName} ({role})</Option>;
              })
            }
          </Dropdown>
        )}
      </div>
    );
  };

  return (
    <div className={classes.formContainer}>
      {kfzForm.steps.map(step => {
        const isStepVisible = step.visibleWhen ? evalCond(step.visibleWhen, formData) : true;
        if (!isStepVisible) return null;

        return (
          <div key={step.name}>
            <div className={classes.sectionTitle}>{step.name}</div>
            <div className={classes.fieldGrid}>
              {step.fields.map(f => renderField(f))}
            </div>
          </div>
        );
      })}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
        <Button appearance="secondary" onClick={onCancel}>Abbrechen</Button>
        <Button appearance="primary" onClick={() => onSave(formData)}>Speichern</Button>
      </div>
    </div>
  );
};

const displayNameOf = (contact: any): string => {
  if (!contact) return "";
  if (contact.displayName) return contact.displayName as string;
  const last = contact.lastname ?? "";
  const first = contact.firstName ?? "";
  if (last && first) return `${last}, ${first}`;
  return last || first || "";
};

const pickPrimaryContact = (item: any): any | null => {
  if (item?.contract?.contact) return item.contract.contact;
  if (Array.isArray(item?.contacts) && item.contacts.length > 0) return item.contacts[0];
  return null;
};

const mapRiskRow = (item: any): Risiko => ({
  RisikoID: Number(item.id),
  RisikoOrt: item.name || item.riskType || `Risiko ${item.id}`,
  VertragID: Number(item.contractID) || 0,
  KontaktID: Number(item.contactID) || 0,
  ZweigID: item.riskType === "KFZ" ? 3 : 0,
  Zweig: item.riskType || "",
  KontaktName: displayNameOf(pickPrimaryContact(item)),
  Vertragsnummer: item.contract?.contractNbr ?? "",
});

const RisikenTabelleDataGrid: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [risiken, setRisiken] = React.useState<Risiko[]>([]);
  const [search, setSearch] = React.useState("");
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set());
  const [backendIds, setBackendIds] = React.useState<Set<number>>(new Set());
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(true);
  const PAGE_SIZE = 50;

  // Spalten-Definition mit createTableColumn
  const columns: TableColumnDefinition<Risiko>[] = React.useMemo(
    () => [
      createTableColumn<Risiko>({
        columnId: "select",
        renderHeaderCell: () => <></>,
      }),
      createTableColumn<Risiko>({
        columnId: "Risikoobjekt",
        renderHeaderCell: () => <>Risikoobjekt</>,
        renderCell: (item) => <>{item.Kennzeichen ?? item.RisikoOrt}</>,
      }),
      createTableColumn<Risiko>({
        columnId: "Vertragsnummer",
        renderHeaderCell: () => <>Vertrag</>,
        renderCell: (item) => <>{item.Vertragsnummer}</>,
      }),
      createTableColumn<Risiko>({
        columnId: "KontaktName",
        renderHeaderCell: () => <>Kunde</>,
        renderCell: (item) => <>{item.KontaktName}</>,
      }),
    ],
    [],
  );

  // ColumnSizingOptions definieren
  const columnSizingOptions: TableColumnSizingOptions = React.useMemo(
    () => ({
      select: { defaultWidth: 48, minWidth: 48 },
      Risikoobjekt: { defaultWidth: 360, minWidth: 200 },
      Vertragsnummer: { defaultWidth: 220, minWidth: 120 },
      KontaktName: { defaultWidth: 360, minWidth: 200 },
    }),
    [],
  );

  // Server-side search + infinite scroll: `risiken` holds the rows loaded so
  // far for the current search term; scrolling appends the next page.
  const filteredRisks = risiken;
  // Handlers for CommandBar
  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  // Selected KFZ risks → enable "KFZ-Vertrag erstellen" button
  const selectedKfzIds = React.useMemo(() => {
    const out: number[] = [];
    for (const id of Array.from(selectedIds)) {
      const r = risiken.find((x) => x.RisikoID === id);
      if (!r) continue;
      const z = (r.Zweig ?? "").toUpperCase();
      if (z.includes("KFZ")) out.push(id);
    }
    return out;
  }, [selectedIds, risiken]);
  const { openWindow } = useWindowManager();
  const handleCreateKfzContract = () => {
    if (selectedKfzIds.length === 0) return;
    // If all selected KFZ risks share a contact, pass it along so the contract
    // page lands on the contact already selected.
    const contactIds = new Set<number>();
    for (const id of selectedKfzIds) {
      const r = risiken.find((x) => x.RisikoID === id);
      if (r?.KontaktID) contactIds.add(r.KontaktID);
    }
    const sharedContactId =
      contactIds.size === 1 ? Array.from(contactIds)[0] : null;
    // Wenn alle selektierten KFZ zum gleichen Kontakt gehören, hat unsere Row
    // (gleiche Liste) den KontaktName bereits — nimm den ersten Treffer.
    const sharedContactName = sharedContactId
      ? risiken.find((r) => r.KontaktID === sharedContactId)?.KontaktName ?? null
      : null;
    openContractFormWindow(openWindow, {
      variant: "kfz",
      contactId: sharedContactId,
      contactName: sharedContactName,
      riskObjectIds: selectedKfzIds,
    });
  };

  // Table-Features inklusive ColumnSizing
  const { getRows, tableRef, columnSizing_unstable } = useTableFeatures(
    { columns, items: filteredRisks },
    [useTableColumnSizing_unstable({ columnSizingOptions })],
  );
  // (Re)load the first page whenever the search term changes (debounced).
  // Infinite scroll then appends further pages via fetchMoreRisks.
  React.useEffect(() => {
    const controller = new AbortController();
    const loadFirstPage = async () => {
      try {
        const res = await fetch(
          apiUrl("risk-objects", { q: search, limit: String(PAGE_SIZE), offset: "0", objektOnly: "true" }),
          { signal: controller.signal },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const mapped: Risiko[] = (Array.isArray(data) ? data : []).map(mapRiskRow);
        setBackendIds(new Set(mapped.map((r) => r.RisikoID)));
        setRisiken(mapped);
        setOffset(mapped.length);
        setHasMore(mapped.length === PAGE_SIZE);
      } catch (error) {
        if ((error as { name?: string })?.name === "AbortError") return;
        console.error("Failed to load risk-objects from backend:", error);
        setRisiken([]);
        setHasMore(false);
      }
    };
    const debounce = setTimeout(loadFirstPage, 250);
    return () => {
      clearTimeout(debounce);
      controller.abort();
    };
  }, [search]);

  const fetchMoreRisks = async () => {
    try {
      const res = await fetch(
        apiUrl("risk-objects", {
          q: search,
          limit: String(PAGE_SIZE),
          offset: String(offset),
          objektOnly: "true",
        }),
      );
      if (!res.ok) {
        setHasMore(false);
        return;
      }
      const data = await res.json();
      const mapped: Risiko[] = (Array.isArray(data) ? data : []).map(mapRiskRow);
      setRisiken((prev) => [...prev, ...mapped]);
      setBackendIds((prev) => {
        const next = new Set(prev);
        mapped.forEach((r) => next.add(r.RisikoID));
        return next;
      });
      setOffset((prev) => prev + mapped.length);
      if (mapped.length < PAGE_SIZE) setHasMore(false);
    } catch (error) {
      console.error("Failed to load more risk-objects:", error);
      setHasMore(false);
    }
  };

  // Auswahl-Handler
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Klick auf eine Risiko-Zeile öffnet die Detail-View.
  const handleEdit = (item: Risiko) => {
    if (item?.RisikoID) navigate(`/risks/${item.RisikoID}`);
  };

  const handleEditClick = () => {
    if (selectedIds.size === 1) {
      const it = selectedIds.values();
      const key = it.next();
      handleEdit(filteredRisks.filter((el) => el.RisikoID === key.value)[0]);
    }
  };
  // Löschen
  const handleDelete = async (id: number) => {
    if (backendIds.has(id)) {
      try {
        await fetch(apiUrl(`risk-objects/${id}`), { method: "DELETE" });
      } catch (error) {
        console.error(`Failed to delete risk-object ${id}:`, error);
      }
    }

    setRisiken((prev) => prev.filter((k) => k.RisikoID !== id));
    setBackendIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const rows = getRows();

  return (
    <div style={{ fontSize: "14px", maxWidth: "100%", overflowX: "auto" }}>
      <CommandBar
        header="Risiken"
        onSearchChange={handleSearchChange}
        onEditClick={handleEditClick}
        canCreate={false}
        canDelete={false}
      />

      {selectedKfzIds.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 12px",
            backgroundColor: "#f4eaf6",
            borderBottom: "1px solid #d6c2db",
          }}
        >
          <span style={{ fontWeight: 600, color: "#341c34" }}>
            {selectedKfzIds.length} KFZ-Risiko{selectedKfzIds.length === 1 ? "" : "s"}{" "}
            ausgewählt
          </span>
          <Button appearance="primary" onClick={handleCreateKfzContract}>
            KFZ-Vertrag aus Auswahl erstellen
          </Button>
        </div>
      )}

      <div
        id="scrollableDivRisks"
        style={{ height: "calc(100vh - 150px)", overflowY: "auto" }}
      >
        <InfiniteScroll
          dataLength={risiken.length}
          next={fetchMoreRisks}
          hasMore={hasMore}
          loader={
            <div style={{ textAlign: "center", padding: 8 }}>
              Lade weitere Risiken...
            </div>
          }
          scrollableTarget="scrollableDivRisks"
          style={{ overflow: "visible" }}
        >
          <Table
            ref={tableRef}
            aria-label="Risiken-Tabelle"
            {...columnSizing_unstable.getTableProps()}
          >
            <TableHeader>
              <TableRow>
                {columns.map((col) => (
                  <TableHeaderCell
                    key={col.columnId}
                    {...columnSizing_unstable.getTableHeaderCellProps(col.columnId)}
                  >
                    {col.columnId === "select" ? null : col.renderHeaderCell()}
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
            {rows.map(({ item }, i) => {
              const kind = resolveRiskKind(item.Zweig);
              const Icon = RISK_ICONS[kind];
              const iconColor = RISK_ICON_COLORS[kind];
              const primary = item.Kennzeichen ?? item.RisikoOrt;
              const secondary = kind === "kfz" ? item.FahrzeugID : undefined;
              return (
                <TableRow
                  key={item.RisikoID}
                  className={i % 2 === 1 ? classes.stripedRow : undefined}
                >
                  <TableCell
                    {...columnSizing_unstable.getTableCellProps("select")}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selectedIds.has(item.RisikoID)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(item.RisikoID)}
                    />
                  </TableCell>
                  <TableCell
                    {...columnSizing_unstable.getTableCellProps("Risikoobjekt")}
                    onClick={() => handleEdit(item)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        minWidth: 0,
                      }}
                    >
                      <Tooltip content={item.Zweig || kind} relationship="label">
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            color: iconColor,
                            flexShrink: 0,
                          }}
                          aria-label={item.Zweig || kind}
                        >
                          <Icon />
                        </span>
                      </Tooltip>
                      <div
                        className={classes.cellEllipsis}
                        title={[primary, secondary].filter(Boolean).join(" • ")}
                        style={{ minWidth: 0 }}
                      >
                        {primary}
                        {secondary && (
                          <span
                            style={{
                              marginLeft: 8,
                              color: tokens.colorNeutralForeground3,
                              fontSize: tokens.fontSizeBase200,
                            }}
                          >
                            {secondary}
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell
                    {...columnSizing_unstable.getTableCellProps("Vertragsnummer")}
                    onClick={() => handleEdit(item)}
                  >
                    <div
                      className={classes.cellEllipsis}
                      title={item.Vertragsnummer}
                    >
                      {item.Vertragsnummer}
                    </div>
                  </TableCell>
                  <TableCell
                    {...columnSizing_unstable.getTableCellProps("KontaktName")}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={classes.cellEllipsis} title={item.KontaktName}>
                      {item.KontaktName}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            </TableBody>
          </Table>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default RisikenTabelleDataGrid;
